import { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import styles from './styles.module.css';
import SearchIcon from '@/assets/icons/search.svg?react';
import ContainerLoader from '@/components/AdminToolComponents/containerLoader';
import { useToast } from '@/context/ToastContext';
import {
  useGetTurbineAlarmEvents,
  useGetTurbineAlarmSubscriptions,
  usePostTurbineAlarmSubscription,
  useDeleteTurbineAlarmSubscription
} from '@/hooks/services/AlarmSubscription';
import { RootState } from '@/store';

interface AlarmItem {
  id: string;
  name: string;
  enabled: boolean;
  eventName: string;
  subscriptionId?: string;
}

export default function AlarmSubscriptionTab() {
  const { id: turbineId } = useParams<{ id: string }>();
  const { showToast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [alarms, setAlarms] = useState<AlarmItem[]>([]);

  const bundleAppId = useSelector(
    (state: RootState) => state?.auditableElements?.bundleAppId
  );

  const {
    data: alarmEvents,
    isLoading: isLoadingEvents,
    error: eventsError
  } = useGetTurbineAlarmEvents({
    eventCode: turbineId || '',
    bundleAppId: bundleAppId || '',
    useQueryOptions: {
      enabled: Boolean(turbineId && bundleAppId)
    }
  });

  const {
    data: alarmSubscriptions,
    isLoading: isLoadingSubscriptions,
    refetch: refetchSubscriptions
  } = useGetTurbineAlarmSubscriptions({
    eventCode: turbineId || '',
    bundleAppId: bundleAppId || '',
    useQueryOptions: {
      enabled: Boolean(turbineId && bundleAppId)
    }
  });

  const postSubscription = usePostTurbineAlarmSubscription();
  const deleteSubscription = useDeleteTurbineAlarmSubscription();

  useEffect(() => {
    if (alarmEvents && Array.isArray(alarmEvents)) {
      let subscriptionsArray: any[] = [];

      if (Array.isArray(alarmSubscriptions)) {
        subscriptionsArray = alarmSubscriptions;
      } else if (
        alarmSubscriptions?.turbines &&
        Array.isArray(alarmSubscriptions.turbines)
      ) {
        const turbineData = alarmSubscriptions.turbines.find(
          (t: any) => t.eventCode === turbineId
        );
        subscriptionsArray = turbineData?.subscriptions || [];
      } else if (alarmSubscriptions?.subscriptions) {
        subscriptionsArray = alarmSubscriptions.subscriptions;
      }

      const subscriptionsMap = new Map<string, string>(
        subscriptionsArray.map((sub: any) => [sub.eventName, sub.id || sub._id])
      );

      const formattedAlarms: AlarmItem[] = alarmEvents.map((event: any) => ({
        id: event.id || event._id || event.eventName,
        name: event.label,
        eventName: event.eventName,
        enabled: subscriptionsMap.has(event.eventName),
        subscriptionId: subscriptionsMap.get(event.eventName)
      }));

      setAlarms(formattedAlarms);
    }
  }, [alarmEvents, alarmSubscriptions, turbineId]);

  useEffect(() => {
    if (eventsError) {
      showToast('Error loading alarms. Please try again.', {
        type: 'error',
        autoClose: 5000
      });
    }
  }, [eventsError, showToast]);

  const filteredAlarms = useMemo(() => {
    if (!searchTerm.trim()) {
      return alarms;
    }
    return alarms.filter(alarm =>
      alarm.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [alarms, searchTerm]);

  const handleToggleAlarm = async (alarm: AlarmItem) => {
    if (!turbineId || !bundleAppId) return;

    try {
      if (alarm.enabled && alarm.subscriptionId) {
        await deleteSubscription.mutateAsync({
          id: alarm.subscriptionId
        });
      } else {
        await postSubscription.mutateAsync({
          bundleAppId,
          eventCode: turbineId,
          eventName: alarm.eventName
        });
      }

      await refetchSubscriptions();
    } catch (error) {
      showToast('Error updating subscription. Please try again.', {
        type: 'info',
        autoClose: 5000
      });
    }
  };

  const columns = useMemo(() => {
    const itemsPerColumn = Math.ceil(filteredAlarms.length / 3);
    return [
      filteredAlarms.slice(0, itemsPerColumn),
      filteredAlarms.slice(itemsPerColumn, itemsPerColumn * 2),
      filteredAlarms.slice(itemsPerColumn * 2)
    ];
  }, [filteredAlarms]);

  if (isLoadingEvents || isLoadingSubscriptions) {
    return (
      <div className={styles.container}>
        <ContainerLoader />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.searchWrapper}>
        <div className={styles.searchContainer}>
          <SearchIcon className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </div>

      <div className={styles.contentWrapper}>
        <div className={styles.tableCard}>
          <div className={styles.tableHeader}>
            <div className={styles.headerColumn}>Alarm</div>
            <div className={styles.headerColumn}>Alarm</div>
            <div className={styles.headerColumn}>Alarm</div>
          </div>
          <div className={styles.tableBody}>
            {columns.map((columnAlarms, columnIndex) => (
              <div key={columnIndex} className={styles.column}>
                {columnAlarms.map(alarm => (
                  <div key={alarm.id} className={styles.alarmRow}>
                    <label className={styles.toggleSwitch}>
                      <input
                        type="checkbox"
                        checked={alarm.enabled}
                        onChange={() => handleToggleAlarm(alarm)}
                        disabled={
                          postSubscription.isPending ||
                          deleteSubscription.isPending
                        }
                        className={styles.toggleInput}
                      />
                      <span className={styles.toggleSlider}></span>
                    </label>
                    <span className={styles.alarmName}>{alarm.name}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
